<template>
  <router-link
    :to="orderRoute"
    class="ejm-order-card d-flex align-items-center gap-3 px-3 py-2 text-reset text-decoration-none">
    <StatusIndicator :status="displayStatus" />
    <div class="flex-fill" style="min-width: 0">
      <div class="fw-medium text-truncate" v-tooltip="props.order.title">{{ props.order.title }}</div>
      <small v-if="props.order.provider_job_id" class="text-muted">#{{ props.order.provider_job_id }}</small>
    </div>
    <div class="d-none d-lg-flex align-items-center gap-2">
      <StatsPill :text="`${props.order.stats_clicks_total || 0} clicks`" :icon="IconClick" />
      <StatsPill :text="`${props.order.stats_impressions_total || 0} impr.`" :icon="IconEye" />
      <StatsPill v-if="budgetText" :text="budgetText" :icon="IconCoins" />
    </div>
    <div class="d-none d-xl-block text-muted text-nowrap">
      <small>{{ formattedDateRange }}</small>
    </div>
  </router-link>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IconClick, IconEye, IconCoins } from '@tabler/icons-vue';
import StatusIndicator from '@/components/generic/StatusIndicator.vue';
import StatsPill from '@/components/generic/StatsPill.vue';
import type { ExternalJobMarketJobOrder } from '@/types/externalJobMarket';
import { getCurrencySign } from '@/locale/format';
import { formatDate } from '@/filters/dateFormat';

const props = defineProps<{ order: ExternalJobMarketJobOrder }>();

const displayStatus = computed(() => {
  if (props.order.is_draft) return 'draft' as const;
  return props.order.lead_order_status as
    'to_be_confirmed' | 'confirmed' | 'running' | 'finished' | 'waiting_to_finish';
});

const orderRoute = computed(() => {
  if (props.order.is_draft) return `/dashboard/orders/create?orderId=${props.order.id}`;
  return { name: 'order.detail.info', params: { id: props.order.id } };
});

const budgetText = computed(() => {
  if (!props.order.budget_gross_total) return '';
  const sign = getCurrencySign(props.order.budget_currency || '');
  const budget = props.order.budget_gross_total / 100;
  return `${sign} ${budget}`;
});

const formattedDateRange = computed(() => {
  if (!props.order.startdate) return '';
  const start = formatDate(props.order.startdate);
  const end = props.order.enddate ? formatDate(props.order.enddate) : '...';
  return `${start} – ${end}`;
});
</script>

<style scoped>
.ejm-order-card {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 6px;
  transition: background-color 0.15s;
}

.ejm-order-card:hover {
  background: rgba(0, 0, 0, 0.05);
}
</style>
